import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    DragIndicator as DragIcon,
    Help as HelpIcon,
} from "@mui/icons-material";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    getMenus,
    createMenu,
    updateMenu,
    deleteMenu,
    updateMenuSortOrder,
} from "../../../api/api";
import { getCurrentBranchId } from "../../../utils/auth";
import { planLimit } from "../../../utils/plan";
import OnboardingTutorialOverlay from "../../../components/OnboardingTutorialOverlay";

// Sortable Table Row Component
const SortableMenuRow = ({ menu, onEdit, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: menu.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
    };

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 50, px: 1 }}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                        '&:hover': {
                            color: 'primary.main',
                        },
                    }}
                >
                    <DragIcon />
                </Box>
            </TableCell>
            <TableCell>{menu.menu_title}</TableCell>
            <TableCell>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: "monospace",
                        color: "primary.main",
                        wordBreak: "break-all",
                    }}
                >
                    {menu.menu_link}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <IconButton
                    onClick={() => onEdit(menu)}
                    color="primary"
                    size="small"
                >
                    <EditIcon />
                </IconButton>
                <IconButton
                    onClick={() => onDelete(menu.id)}
                    color="error"
                    size="small"
                >
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

const MenuManagement = () => {
    const branchId = getCurrentBranchId();
    const [menus, setMenus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
    const [openDialog, setOpenDialog] = useState(false);
    const [editingMenu, setEditingMenu] = useState(null);
    const [formData, setFormData] = useState({
        menu_title: "",
        menu_link: "",
    });
    const [runTutorial, setRunTutorial] = useState(false);
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchMenus();
    }, []);


    const fetchMenus = async () => {
        try {
            setLoading(true);
            const response = await getMenus(branchId);
            if (response.success) {
                setMenus(response.data);
            } else {
                setToast({ open: true, message: "Failed to fetch menu items", severity: "error" });
            }
        } catch (err) {
            setToast({ open: true, message: "Error loading menu items", severity: "error" });
            console.error("Error fetching menus:", err);
        } finally {
            setLoading(false);
        }
    };

    // Plan-based menu-link cap (null = unlimited).
    const maxMenuLinks = planLimit("max_menu_links");
    const menuLimitReached =
        maxMenuLinks !== null && maxMenuLinks !== undefined && menus.length >= maxMenuLinks;

    const handleCreateMenu = () => {
        if (menuLimitReached) {
            setToast({
                open: true,
                message: `Your current plan allows up to ${maxMenuLinks} menu link(s). Upgrade your plan to add more.`,
                severity: "warning",
            });
            return;
        }
        setEditingMenu(null);
        setFormData({ menu_title: "", menu_link: "" });
        setOpenDialog(true);
    };

    const handleEditMenu = (menu) => {
        setEditingMenu(menu);
        setFormData({
            menu_title: menu.menu_title,
            menu_link: menu.menu_link,
        });
        setOpenDialog(true);
    };

    const handleDeleteMenu = async (id) => {
        if (window.confirm("Are you sure you want to delete this menu item?")) {
            try {
                const response = await deleteMenu(id);
                if (response.success) {
                    setMenus(menus.filter((menu) => menu.id !== id));
                    setToast({ open: true, message: "Menu item deleted successfully", severity: "success" });
                } else {
                    setToast({ open: true, message: "Failed to delete menu item", severity: "error" });
                }
            } catch (err) {
                setToast({ open: true, message: "Error deleting menu item", severity: "error" });
                console.error("Error deleting menu:", err);
            }
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setEditingMenu(null);
        setFormData({ menu_title: "", menu_link: "" });
    };

    const handleSave = async () => {
        try {
            const data = {
                menu_title: formData.menu_title,
                menu_link: formData.menu_link,
                branch_id: branchId,
            };

            console.log('Saving menu:', data);

            let response;
            if (editingMenu) {
                response = await updateMenu(editingMenu.id, formData);
            } else {
                response = await createMenu(data);
            }

            console.log('Menu save response:', response);

            if (response.success) {
                await fetchMenus();
                setToast({ open: true, message: editingMenu ? "Menu item updated successfully" : "Menu item created successfully", severity: "success" });
                handleDialogClose();
            } else {
                const errorMsg = response.error || response.errors || "Failed to save menu item";
                setToast({ open: true, message: errorMsg, severity: "error" });
            }
        } catch (err) {
            console.error("Error saving menu:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Error saving menu item";
            setToast({ open: true, message: errorMsg, severity: "error" });
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = menus.findIndex((menu) => menu.id === active.id);
        const newIndex = menus.findIndex((menu) => menu.id === over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const newItems = arrayMove(menus, oldIndex, newIndex);
        setMenus(newItems);

        // Update sort order in backend
        try {
            const menusWithNewOrder = newItems.map((menu, index) => ({
                id: parseInt(menu.id, 10),
                sort_order: parseInt(index, 10),
            }));

            console.log('Updating menu sort order:', menusWithNewOrder);
            const response = await updateMenuSortOrder(menusWithNewOrder);
            
            if (response.success) {
                setToast({ open: true, message: "Menu order updated successfully", severity: "success" });
            } else {
                const errorMsg = response.error || response.errors || "Failed to update menu order";
                setToast({ open: true, message: errorMsg, severity: "error" });
                // Revert the change
                setMenus(menus);
            }
        } catch (err) {
            console.error("Error updating menu sort order:", err);
            const errorMsg = err.error || err.response?.data?.error || err.message || "Failed to update menu order";
            setToast({ open: true, message: errorMsg, severity: "error" });
            // Revert the change
            setMenus(menus);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Tutorial overlay (driver.js) – same UX as /home/panel */}
            <OnboardingTutorialOverlay
                page="menu_management"
                run={runTutorial}
                onPhaseComplete={() => setRunTutorial(false)}
                onClose={() => setRunTutorial(false)}
            />

            <Box>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h4" component="h1">
                            Menu Management
                        </Typography>
                        <IconButton
                            onClick={() => setRunTutorial(true)}
                            title="Show Tutorial"
                            color="primary"
                        >
                            <HelpIcon />
                        </IconButton>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateMenu}
                        disabled={menuLimitReached}
                        title={
                            menuLimitReached
                                ? `Your plan allows up to ${maxMenuLinks} menu link(s). Upgrade to add more.`
                                : undefined
                        }
                        data-onboarding-id="onboarding-menu-add"
                    >
                        Add New Menu Item
                    </Button>
                </Box>

                <Snackbar
                    open={toast.open}
                    autoHideDuration={3000}
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert
                        onClose={() => setToast((t) => ({ ...t, open: false }))}
                        severity={toast.severity}
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {toast.message}
                    </Alert>
                </Snackbar>

                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        boxShadow: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                        Manage your menu items that appear in the customer-facing interface. 
                        You can create, edit, delete, and reorder menu items by dragging them. 
                        Each menu item consists of a title and a link (URL or path) that customers can access.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        <strong>Tip:</strong> Drag the menu items using the drag handle (⋮⋮) to reorder them. 
                        The order you set here will be reflected in the customer interface.
                    </Typography>
                </Box>

                <TableContainer component={Paper} data-onboarding-id="onboarding-menu-list">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 50 }}></TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Menu Title</TableCell>
                                    <TableCell sx={{ fontWeight: "bold" }}>Menu Link</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: "bold" }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <SortableContext
                                    items={menus.map((menu) => menu.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {menus.map((menu) => (
                                        <SortableMenuRow
                                            key={menu.id}
                                            menu={menu}
                                            onEdit={handleEditMenu}
                                            onDelete={handleDeleteMenu}
                                        />
                                    ))}
                                </SortableContext>
                            </TableBody>
                        </Table>
                    </DndContext>
                </TableContainer>

                {menus.length === 0 && (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            No menu items found. Create your first menu item to get started!
                        </Typography>
                    </Box>
                )}

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openDialog}
                    onClose={handleDialogClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {editingMenu ? "Edit Menu Item" : "Create New Menu Item"}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            <TextField
                                fullWidth
                                label="Menu Title"
                                value={formData.menu_title}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        menu_title: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                            />
                            <TextField
                                fullWidth
                                label="Menu Link"
                                value={formData.menu_link}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        menu_link: e.target.value,
                                    })
                                }
                                margin="normal"
                                required
                                helperText="URL or path for the menu item (e.g., /home/about, https://example.com)"
                                inputProps={{
                                    maxLength: 500,
                                }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose}>Cancel</Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!formData.menu_title || !formData.menu_link}
                        >
                            {editingMenu ? "Update" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};

export default MenuManagement;

